import { Chat, FeatureFlag } from "../types/chat";
import { tokenManager } from "../auth/tokenManager";

// Use relative URLs in production, localhost in development
const API_BASE =
  process.env.NODE_ENV === "production"
    ? "" // Root path for dedicated domain deployment
    : "http://localhost:8090";

/**
 * 🏢 MULTI-TENANT SAAS: Token Health Manager Integration
 *
 * Uses the centralized TokenManager for:
 * - Health detection
 * - Automatic recovery
 * - User-friendly error messages
 * - Token lifecycle management
 */
// Stored separately from tokenManager to allow force-refresh with options
let rawGetAccessTokenFn: ((options?: Record<string, unknown>) => Promise<string>) | null = null;

export function setAccessTokenFn(fn: (options?: Record<string, unknown>) => Promise<string>) {
  rawGetAccessTokenFn = fn;
  tokenManager.setAccessTokenFunction(() => fn());
}

/**
 * Get authentication headers with health monitoring
 *
 * For authenticated endpoints: Includes Bearer token
 * For optional endpoints: Returns without token (graceful degradation)
 *
 * @param options - Auth options
 * @returns Headers with Authorization if token available
 */
async function getAuthHeaders(options?: {
  optional?: boolean;  // If true, don't log warnings when token unavailable
}): Promise<HeadersInit> {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  const token = await tokenManager.getToken({ silent: true });

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  } else if (!options?.optional) {
    // Only log warnings for non-optional endpoints
    const healthStatus = tokenManager.getHealthStatus();
    if (healthStatus?.error) {
      console.warn(`[Auth] ${healthStatus.error}`);
    }
  }

  return headers;
}

/**
 * Authenticated fetch with automatic token handling
 *
 * Use this for API calls that require authentication.
 * For optional auth endpoints, use regular fetch instead.
 */
export async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const authHeaders = await getAuthHeaders();

  const mergedHeaders: HeadersInit = {
    ...options.headers,
    ...authHeaders,
  };

  const response = await fetch(url, { ...options, headers: mergedHeaders });

  // On 401, bypass the cache and retry once with a freshly-fetched token.
  // This recovers stale cached tokens when the refresh token is still valid.
  if (response.status === 401 && rawGetAccessTokenFn) {
    try {
      const freshToken = await rawGetAccessTokenFn({ cacheMode: 'off' });
      if (freshToken) {
        return fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${freshToken}`,
          },
        });
      }
    } catch {
      // Refresh failed (expired refresh token etc.) — fall through to return 401
    }
  }

  return response;
}

export const orchestratorApi = {
  // Chat endpoints
  async getChats(
    status?: string,
    limit?: number,
    offset?: number,
    entityId?: string,
    product?: string,
    leadDateFrom?: string,
    leadDateTo?: string,
  ): Promise<Chat[]> {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (limit) params.set("limit", limit.toString());
    if (offset) params.set("offset", offset.toString());
    if (entityId) params.set("entityId", entityId);
    if (product) params.set("product", product);
    if (leadDateFrom) params.set("lead_date_from", leadDateFrom);
    if (leadDateTo) params.set("lead_date_to", leadDateTo);

    const url = `${API_BASE}/chats${params.toString() ? "?" + params.toString() : ""}`;

    const response = await authFetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch chats: ${response.statusText}`);
    }
    const data = await response.json();
    return data.chats;
  },

  async getChatMessages(chatId: string, limit: number, entityId?: string): Promise<any> {
    // Filter by entity_id - only show messages from the selected entity
    const url = entityId
      ? `${API_BASE}/api/chats/${chatId}/messages?limit=${limit}&entity_id=${entityId}`
      : `${API_BASE}/api/chats/${chatId}/messages?limit=${limit}`;

    // Requires auth: /api routes now enforce requireAuth + requireTenant (Chunk A, 2026-07-06).
    // authFetch attaches the bearer token and retries once on 401 with a fresh token.
    const response = await authFetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch messages: ${response.statusText}`);
    }

    return response.json();
  },

  async getChatPauseStatus(chatId: string): Promise<{
    ok: boolean;
    ai_paused: boolean;
    ai_paused_until: string | null;
    last_human_intervention_at: string | null;
    minutes_remaining: number;
  }> {
    // Requires auth: /api routes now enforce requireAuth + requireTenant (Chunk A, 2026-07-06).
    // authFetch attaches the bearer token and retries once on 401 with a fresh token.
    const response = await authFetch(`${API_BASE}/api/chats/${chatId}/pause-status`);

    if (!response.ok) {
      throw new Error(`Failed to fetch pause status: ${response.statusText}`);
    }

    return response.json();
  },

  async updateChatStatus(
    chatId: string,
    status: string,
    oldStatus?: string,
  ): Promise<Chat> {
    const response = await fetch(`${API_BASE}/chats/${chatId}/status`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ status, old_status: oldStatus }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update chat status: ${response.statusText}`);
    }

    const data = await response.json();
    return data.chat;
  },

  async sendMessage(request: {
    request_id: string;
    chat_id: string;
    body?: string;
    media?: {
      type: "image" | "doc" | "audio";
      file: File;
      meta?: Record<string, any>;
    };
  }): Promise<{ ok: boolean; message_id?: string; error?: string }> {
    const authHeaders = await this.getAuthHeaders();

    // If there's a file attachment, send as FormData
    if (request.media) {
      // Create FormData for file upload
      const formData = new FormData();

      // Add basic fields
      formData.append('request_id', request.request_id);
      formData.append('chat_id', request.chat_id);

      if (request.body) {
        formData.append('body', request.body);
      }

      formData.append('media_type', request.media.type);
      formData.append('file', request.media.file);

      if (request.media.meta) {
        formData.append('media_meta', JSON.stringify(request.media.meta));
      }

      const response = await fetch(`${API_BASE}/send`, {
        method: "POST",
        headers: authHeaders,
        body: formData,
        // Note: Don't set Content-Type header when using FormData
        // Browser will set it automatically with boundary
      });

      const data = await response.json();
      console.log('[DEBUG] Response received from orchestrator (FormData):', data);

      if (!response.ok) {
        return { ok: false, error: data.error || response.statusText };
      }

      return { ok: true, message_id: data.message_id };
    } else {
      // For text-only messages, send as JSON
      const response = await fetch(`${API_BASE}/send`, {
        method: "POST",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          request_id: request.request_id,
          chat_id: request.chat_id,
          body: request.body,
        }),
      });

      const data = await response.json();
      console.log('[DEBUG] Response received from orchestrator (JSON):', data);

      if (!response.ok) {
        return { ok: false, error: data.error || response.statusText };
      }

      return { ok: true, message_id: data.message_id };
    }
  },

  async syncChat(
    chatId: string,
    options?: { messagesPerChat?: number },
  ): Promise<{ success: boolean; message?: string }> {
    const authHeaders = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE}/admin/sync-chat`, {
      method: "POST",
      headers: {
        ...authHeaders,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        chatId,
        ...(options?.messagesPerChat
          ? { messagesPerChat: options.messagesPerChat }
          : {}),
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.error || `Failed to sync chat: ${response.statusText}`,
      );
    }

    return data;
  },

  // Feature flag endpoints
  async getFeatureFlags(): Promise<FeatureFlag[]> {
    const response = await authFetch(`${API_BASE}/flags`);
    if (!response.ok) {
      throw new Error(`Failed to fetch feature flags: ${response.statusText}`);
    }
    const data = await response.json();
    return data.flags;
  },

  async updateFeatureFlag(
    name: string,
    enabled: boolean,
  ): Promise<FeatureFlag> {
    const response = await authFetch(`${API_BASE}/flags`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name, enabled }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update feature flag: ${response.statusText}`);
    }

    const data = await response.json();
    return data.flag;
  },

  // WhatsApp endpoints
  async getWhatsAppStatus(entityId?: string): Promise<{
    connected: boolean;
    status: string;
    initialized?: boolean;
    qr_required?: boolean;
    worker_reachable?: boolean;
  }> {
    const params = new URLSearchParams();
    if (entityId) {
      params.set('entityId', entityId);
    }
    const url = `${API_BASE}/admin/whatsapp/status${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to get WhatsApp status: ${response.statusText}`);
    }
    return response.json();
  },

  async getCurrentUser(entityId?: string): Promise<{ ok: boolean; user?: any; error?: string }> {
    const params = new URLSearchParams();
    if (entityId) {
      params.set('entityId', entityId);
    }
    const url = `${API_BASE}/whoami${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to get current user: ${response.statusText}`);
    }
    return response.json();
  },

  async createGroup(
    groupName: string,
    participants: string[],
    entityId?: string
  ): Promise<{
    ok: boolean;
    chatId?: string;
    groupName?: string;
    participantCount?: number;
    message?: string;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const params = new URLSearchParams();
      if (entityId) {
        params.set('entityId', entityId);
      }

      const url = `${API_BASE}/api/whatsapp/groups/create${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ groupName, participants }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `Failed to create group: ${response.statusText}`,
        };
      }

      const data = await response.json();
      return {
        ok: data.ok || true,
        chatId: data.chatId,
        groupName: data.groupName,
        participantCount: data.participantCount,
        message: data.message,
      };
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  async getQrCode(entityId?: string, tenantId?: string): Promise<{
    qrCodeDataUrl: string;
    validation?: {
      isValid: boolean;
      isReadable: boolean;
      content?: string;
      formatValid: boolean;
    };
    freshness?: string;
    recommendations?: string[];
    timestamp?: string;
    age_minutes?: number;
  } | { error: string }> {
    console.log('[QR] Fetching QR code from orchestrator', { entityId, tenantId });

    // 🏢 MULTI-TENANT: Use tenant-scoped endpoint if tenant ID is provided
    let url: string;
    if (tenantId && entityId) {
      // Use new multi-tenant endpoint with authentication
      url = `${API_BASE}/api/whatsapp/${tenantId}/${entityId}/qr?t=${Date.now()}`;
    } else if (entityId) {
      // Fallback to legacy endpoint for backward compatibility
      url = `${API_BASE}/admin/qr-code?t=${Date.now()}&entityId=${entityId}`;
    } else {
      // No entity specified, use default
      url = `${API_BASE}/admin/qr-code?t=${Date.now()}`;
    }

    const authHeaders = await this.getAuthHeaders();
    const response = await fetch(url, {
      headers: {
        ...authHeaders,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });

    console.log('[QR] Received response from orchestrator', response);
    if (!response.ok) {
      console.error('[QR] Orchestrator returned error', response);
      return { error: `QR code not available: ${response.statusText}` };
    }

    // Check if response is an image (PNG)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('image')) {
      // Convert image blob to base64 data URL
      const blob = await response.blob();
      const dataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });

      console.log('[QR] Received QR code image', { size: blob.size });
      return { qrCodeDataUrl: dataUrl as string };
    }

    // Otherwise try to parse as JSON
    try {
      const data = await response.json();
      console.log('[QR] Parsed QR code data', data);
      return data;
    } catch (e) {
      console.error('[QR] Failed to parse response as JSON or image', e);
      return { error: 'Invalid response format from QR code endpoint' };
    }
  },

  async login(): Promise<{ success: boolean; message: string }> {
    const authHeaders = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE}/api/whatsapp/login`, {
      method: "POST",
      headers: authHeaders,
    });
    if (!response.ok) {
      throw new Error(`Failed to login: ${response.statusText}`);
    }
    return response.json();
  },

  async logout(entityId?: string): Promise<{ success: boolean; message: string }> {
    const authHeaders = await this.getAuthHeaders();
    const body = entityId ? { entityId } : {};
    const response = await fetch(`${API_BASE}/api/whatsapp/logout`, {
      method: "POST",
      headers: {
        ...authHeaders,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Error(`Failed to logout: ${response.statusText}`);
    }
    return response.json();
  },

  // QR Code management endpoints
  async getQrCodeTimestamp(entityId?: string): Promise<{
    exists: boolean;
    timestamp?: string;
    age_minutes?: number;
    age_status?: string;
  }> {
    const params = new URLSearchParams({ t: Date.now().toString() });
    if (entityId) {
      params.set('entityId', entityId);
    }
    const response = await fetch(`${API_BASE}/admin/qr-code/timestamp?${params.toString()}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    if (!response.ok) {
      throw new Error(
        `Failed to get QR code timestamp: ${response.statusText}`,
      );
    }
    return response.json();
  },

  async refreshQrCode(entityId?: string, tenantId?: string): Promise<{
    ok: boolean;
    message: string;
    timestamp: string;
    rateLimit?: {
      maxAttempts: number;
      windowMinutes: number;
      attemptsMade: number;
      remainingTime: number;
    };
  }> {
    // 🏢 MULTI-TENANT: Use tenant-scoped endpoint if tenant ID is provided
    let url: string;
    const authHeaders = await this.getAuthHeaders();

    if (tenantId && entityId) {
      // Use new multi-tenant endpoint with authentication
      url = `${API_BASE}/whatsapp/${tenantId}/${entityId}/refresh-qr`;
      const response = await fetch(url, {
        method: "POST",
        headers: authHeaders,
      });
      const data = await response.json();
      return {
        ok: response.ok && data.success !== false,
        message: data.message || '',
        timestamp: new Date().toISOString(),
        rateLimit: data.rateLimit
      };
    } else {
      // Fallback to legacy endpoint for backward compatibility
      const body = entityId ? { entityId } : {};
      const response = await fetch(`${API_BASE}/admin/qr-code/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      return {
        ok: response.ok && data.success !== false,
        message: data.message || '',
        timestamp: new Date().toISOString(),
        rateLimit: data.rateLimit
      };
    }
  },

  // Entity information endpoint
  async getCurrentEntity(): Promise<{
    ok: boolean;
    user?: {
      id: string;
      name: string;
      phone: string;
      pushname?: string;
      platform?: string;
    };
    error?: string;
    message?: string;
    qr_required?: boolean;
    backend_issue?: boolean;
  }> {
    try {
      const response = await fetch(`${API_BASE}/whoami`, {
        // Prevent fetch from throwing on HTTP error status codes
        // and handle 503 as a normal response
      });

      if (response.status === 503) {
        // 503 is expected when WhatsApp isn't authenticated - not an error
        try {
          const errorData = await response.json();
          return {
            ok: false,
            error: "WhatsApp authentication required",
            qr_required: errorData.qr_required || true,
          };
        } catch {
          return {
            ok: false,
            error: "WhatsApp authentication required",
            qr_required: true,
          };
        }
      }

      if (!response.ok) {
        return {
          ok: false,
          error: `Failed to get current entity: ${response.statusText}`,
        };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // Handle network errors gracefully
      console.warn("getCurrentEntity network error (non-blocking):", error);
      return {
        ok: false,
        error: `Network error: ${error}`,
        qr_required: false,
      };
    }
  },

  // WhatsApp chat sync methods
  async syncWhatsAppChats(
    limit: number,
    messagesPerChat: number,
    entityId?: string,
  ): Promise<{
    success: boolean;
    jobId?: string;
    status?: string;
    total?: number;
    synced?: number;
    errors?: number;
    message?: string;
    error?: string;
    qrRefreshUrl?: string;
    messagesSync?: {
      totalMessages: number;
    };
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/admin/sync-whatsapp-chats`, {
        method: "POST",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ limit, messagesPerChat, entityId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error:
            errorData.details ||
            errorData.message ||
            `HTTP ${response.status}: ${response.statusText}`,
          qrRefreshUrl: errorData.qrRefreshUrl,
        };
      }

      return await response.json();
    } catch (error) {
      return { success: false, error: `Network error: ${error}` };
    }
  },

  async syncWhatsAppChat(
    chatId: string,
    messagesPerChat: number = 20,
  ): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/admin/sync-chat`, {
        method: "POST",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ chatId, messagesPerChat }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error:
            errorData.error ||
            `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return { success: false, error: `Network error: ${error}` };
    }
  },

  // Job status polling
  async getSyncJobStatus(jobId: string): Promise<{
    success: boolean;
    job?: {
      id: string;
      type: string;
      status: string;
      totalChats: number;
      chatsProcessed: number;
      chatsSynced: number;
      chatsErrors: number;
      totalMessages: number;
      errorMessage?: string;
      resultData?: any;
      createdAt: string;
      startedAt?: string;
      completedAt?: string;
    };
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/admin/sync-jobs/${jobId}`, {
        headers: authHeaders,
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch job status: ${response.statusText}`);
      }
      return response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  },

  // Lead agent configuration endpoints
  async getLeadConfig(): Promise<{
    enabled: boolean;
    config: {
      targetChat: string;
    };
  }> {
    const response = await fetch(`${API_BASE}/admin/lead-config`);
    if (!response.ok) {
      throw new Error(`Failed to get lead config: ${response.statusText}`);
    }
    return response.json();
  },

  async updateLeadConfig(targetChat: string): Promise<{
    success: boolean;
    config: {
      targetChat: string;
    };
  }> {
    const response = await fetch(`${API_BASE}/admin/lead-config`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ targetChat }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update lead config: ${response.statusText}`);
    }
    return response.json();
  },

  // Agent Chat Exclusions endpoints
  async getAgentExclusions(agentId: string): Promise<{
    ok: boolean;
    exclusions?: {
      group: Array<{
        id: number;
        participant_phone: string;
        reason: string;
        is_active: boolean;
        created_at: string;
      }>;
      direct: Array<{
        id: number;
        direct_chat_phone: string;
        reason: string;
        is_active: boolean;
        created_at: string;
      }>;
      groupByName: Array<{
        id: number;
        group_chat_name: string;
        reason: string;
        is_active: boolean;
        created_at: string;
      }>;
    };
    error?: string;
  }> {
    const authHeaders = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE}/api/agents/${encodeURIComponent(agentId)}/exclusions`, {
      headers: authHeaders,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        ok: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    return response.json();
  },

  async addAgentExclusion(agentId: string, exclusion: {
    exclusion_type: 'group' | 'direct' | 'both' | 'group-by-name';
    participant_phone?: string;
    direct_chat_phone?: string;
    group_chat_name?: string;
    reason?: string;
    entity_id: string;
  }): Promise<{
    ok: boolean;
    exclusion?: any;
    error?: string;
  }> {
    const authHeaders = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE}/api/agents/${encodeURIComponent(agentId)}/exclusions`, {
      method: 'POST',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(exclusion),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        ok: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    return response.json();
  },

  async updateAgentExclusion(agentId: string, exclusionId: number, updates: {
    exclusion_type?: 'group' | 'direct' | 'both';
    participant_phone?: string;
    direct_chat_phone?: string;
    reason?: string;
    is_active?: boolean;
  }): Promise<{
    ok: boolean;
    exclusion?: any;
    error?: string;
  }> {
    const authHeaders = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE}/api/agents/${encodeURIComponent(agentId)}/exclusions/${exclusionId}`, {
      method: 'PUT',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        ok: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    return response.json();
  },

  async deleteAgentExclusion(agentId: string, exclusionId: number): Promise<{
    ok: boolean;
    error?: string;
  }> {
    const authHeaders = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE}/api/agents/${encodeURIComponent(agentId)}/exclusions/${exclusionId}`, {
      method: 'DELETE',
      headers: authHeaders,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        ok: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    return response.json();
  },

  async copyAgentExclusions(
    agentId: string,
    sourceAgentId: string,
    entityId: string
  ): Promise<{
    ok: boolean;
    copied?: number;
    skipped?: number;
    error?: string;
  }> {
    const authHeaders = await this.getAuthHeaders();
    const response = await fetch(
      `${API_BASE}/api/agents/${encodeURIComponent(agentId)}/exclusions/copy-from/${encodeURIComponent(sourceAgentId)}`,
      {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ entity_id: entityId }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        ok: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    return response.json();
  },

  // Group chat search for exclusions (searches all groups in tenant)
  async searchGroups(query: string): Promise<{
    ok: boolean;
    groups?: Array<{
      chat_id: string;
      phone_number: string;
      name: string;
    }>;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const params = new URLSearchParams({ query });

      const response = await fetch(`${API_BASE}/api/groups/search?${params}`, {
        headers: authHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  // Agent Assignment toggle endpoint
  async toggleAgentAssignment(assignmentId: string, tenantId: string, enabled: boolean): Promise<{
    ok: boolean;
    message?: string;
    enabled?: boolean;
    error?: string;
  }> {
    const authHeaders = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE}/api/entity-assignments/${assignmentId}/toggle`, {
      method: 'PUT',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ tenantId, enabled }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        ok: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    return response.json();
  },

  // Translation endpoints
  async translateText(text: string, targetLanguage: string): Promise<{
    ok: boolean;
    originalText?: string;
    translatedText?: string;
    targetLanguage?: string;
    targetLanguageName?: string;
    model?: string;
    error?: string;
    details?: string;
    supportedLanguages?: Record<string, string>;
  }> {
    const authHeaders = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE}/api/translate`, {
      method: "POST",
      headers: {
        ...authHeaders,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ text, targetLanguage }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        ok: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        details: errorData.details,
        supportedLanguages: errorData.supportedLanguages,
      };
    }

    return await response.json();
  },

  // Token usage endpoints
  async getTokenUsageDashboard(period: string = '24h'): Promise<any> {
    const authHeaders = await this.getAuthHeaders();
    const url = `${API_BASE}/api/token-usage/dashboard?period=${period}`;
    const response = await fetch(url, { headers: authHeaders });
    if (!response.ok) {
      throw new Error(`Failed to fetch token usage dashboard: ${response.statusText}`);
    }
    const data = await response.json();
    console.log('Dashboard API response:', data);
    return data;
  },

  async getTokenUsageHourly(params: {
    startDate?: string;
    endDate?: string;
    serviceName?: string;
    processName?: string;
    provider?: string;
    modelName?: string;
  } = {}): Promise<any> {
    const authHeaders = await this.getAuthHeaders();
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) searchParams.set(key, value);
    });

    const url = `${API_BASE}/api/token-usage/hourly${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await fetch(url, { headers: authHeaders });
    if (!response.ok) {
      throw new Error(`Failed to fetch hourly token usage: ${response.statusText}`);
    }
    return await response.json();
  },

  async getTokenUsageDaily(params: {
    startDate?: string;
    endDate?: string;
    serviceName?: string;
    processName?: string;
    provider?: string;
    modelName?: string;
  } = {}): Promise<any> {
    const authHeaders = await this.getAuthHeaders();
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) searchParams.set(key, value);
    });

    const url = `${API_BASE}/api/token-usage/daily${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await fetch(url, { headers: authHeaders });
    if (!response.ok) {
      throw new Error(`Failed to fetch daily token usage: ${response.statusText}`);
    }
    return await response.json();
  },

  async getTokenUsageRealtime(hours: number = 1): Promise<any> {
    const authHeaders = await this.getAuthHeaders();
    const url = `${API_BASE}/api/token-usage/realtime?hours=${hours}`;
    const response = await fetch(url, { headers: authHeaders });
    if (!response.ok) {
      throw new Error(`Failed to fetch real-time token usage: ${response.statusText}`);
    }
    const data = await response.json();
    console.log('Realtime API response:', data);
    return data;
  },

  // Generic GET method for other API calls
  async get(endpoint: string, options?: { params?: Record<string, string> }): Promise<any> {
    const authHeaders = await this.getAuthHeaders();
    const url = new URL(endpoint, API_BASE);
    if (options?.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value) url.searchParams.set(key, value);
      });
    }

    const response = await fetch(url.toString(), { headers: authHeaders });
    if (!response.ok) {
      throw new Error(`Failed to fetch ${endpoint}: ${response.statusText}`);
    }
    return await response.json();
  },

  // Entity Management

  // Create new entity
  async createEntity(name: string, phone: string): Promise<{
    success: boolean;
    entity?: any;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/entities`, {
        method: "POST",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, phone }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          error: error.error || `Failed to create entity: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  // Update entity
  async updateEntity(id: string, name: string, phone: string, timezone?: string): Promise<{
    success: boolean;
    entity?: any;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/entities/${id}`, {
        method: "PUT",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, phone, ...(timezone ? { timezone } : {}) }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          error: error.error || `Failed to update entity: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  // Delete entity (archive)
  async deleteEntity(id: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/entities/${id}`, {
        method: "DELETE",
        headers: authHeaders,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          error: error.error || `Failed to archive entity: ${response.statusText}`,
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  // Get archived entities
  async getArchivedEntities(): Promise<{
    success: boolean;
    entities?: any[];
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/entities/archived`, {
        headers: authHeaders
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          error: error.error || `Failed to get archived entities: ${response.statusText}`,
        };
      }

      const data = await response.json();
      return {
        success: data.ok,
        entities: data.entities,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  // Restore archived entity
  async restoreEntity(id: string): Promise<{
    success: boolean;
    entity?: any;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/entities/${id}/restore`, {
        method: "POST",
        headers: authHeaders,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          error: error.error || `Failed to restore entity: ${response.statusText}`,
        };
      }

      const data = await response.json();
      return {
        success: data.ok,
        entity: data.entity,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  // Permanently delete an archived entity and all its data
  async permanentlyDeleteEntity(id: string): Promise<{
    success: boolean;
    stats?: {
      messages: number;
      chats: number;
      recipients: number;
      campaigns: number;
    };
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/entities/${id}/permanent`, {
        method: "DELETE",
        headers: authHeaders,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          error: error.error || `Failed to permanently delete entity: ${response.statusText}`,
        };
      }

      const data = await response.json();
      return {
        success: data.ok,
        stats: data.stats,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  // WebSocket connection
  createWebSocket(): WebSocket {
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsHost =
      process.env.NODE_ENV === "production"
        ? window.location.host
        : "localhost:8090";
    return new WebSocket(`${wsProtocol}//${wsHost}/stream`);
  },

  // ==================== KNOWLEDGE BASE API ====================

  /**
   * Get all documents in the knowledge base
   */
  async getKnowledgeBaseDocuments(): Promise<{
    ok: boolean;
    documents?: Array<{
      id: number;
      filename: string;
      original_filename: string;
      file_size: number;
      mime_type: string;
      description: string | null;
      source: string | null;
      chunk_count: number;
      upload_date: string;
      processing_status: string;
      status: string;
    }>;
    count?: number;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/knowledge-base/documents`, {
        headers: authHeaders
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Upload a document to the knowledge base
   */
  async uploadKnowledgeBaseDocument(
    file: File,
    metadata: {
      description: string;
      source: string;
    }
  ): Promise<{
    ok?: boolean;
    success?: boolean;
    message?: string;
    document_id?: number;
    chunks_created?: number;
    processing_time_ms?: number;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const formData = new FormData();
      formData.append("file", file);
      formData.append("description", metadata.description);
      formData.append("source", metadata.source);
      formData.append("uploaded_by", "admin");

      const response = await fetch(`${API_BASE}/api/knowledge-base/upload`, {
        method: "POST",
        headers: authHeaders,
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Archive a document from the knowledge base (soft delete)
   */
  async deleteKnowledgeBaseDocument(
    documentId: number
  ): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/knowledge-base/documents/${documentId}`, {
        method: "DELETE",
        headers: authHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Permanently delete a document from the knowledge base (hard delete)
   */
  async permanentlyDeleteKnowledgeBaseDocument(
    documentId: number
  ): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/knowledge-base/documents/${documentId}/permanent`, {
        method: "DELETE",
        headers: authHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Get document content for viewing
   */
  async getKnowledgeBaseDocumentContent(
    documentId: number
  ): Promise<{
    ok: boolean;
    content?: string;
    filename?: string;
    mime_type?: string;
    file_size?: number;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/knowledge-base/documents/${documentId}/content`, {
        headers: authHeaders
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Update a document in the knowledge base
   */
  async updateKnowledgeBaseDocument(
    documentId: number,
    data: {
      description?: string;
      source?: string;
      content_text?: string;
    }
  ): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/knowledge-base/documents/${documentId}`, {
        method: "PATCH",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Search the knowledge base
   */
  async searchKnowledgeBase(
    query: string,
    options?: {
      top_k?: number;
      min_score?: number;
      category?: string;
      authority?: string;
    }
  ): Promise<{
    success: boolean;
    results?: Array<{
      chunk_id: number;
      document_id: number;
      content: string;
      similarity_score: number;
      document_filename: string;
      product_category?: string;
      authority?: string;
    }>;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/knowledge-base/search`, {
        method: "POST",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query, ...options }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Get knowledge base statistics
   */
  async getKnowledgeBaseStats(): Promise<{
    ok: boolean;
    stats?: {
      total_documents: number;
      total_chunks: number;
      total_searches: number;
      categories: Record<string, number>;
      authorities: Record<string, number>;
    };
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/knowledge-base/stats`, {
        headers: authHeaders
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  // ============================================
  // CURATED QA LIBRARY API
  // ============================================

  /**
   * Get all curated Q&A pairs
   */
  async getCuratedQAPairs(filters?: {
    category?: string;
    is_active?: boolean;
    tenant_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    success: boolean;
    pairs?: any[];
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const params = new URLSearchParams();
      if (filters?.category) params.set('category', filters.category);
      if (filters?.is_active !== undefined) params.set('is_active', String(filters.is_active));
      if (filters?.tenant_id) params.set('tenant_id', filters.tenant_id);
      if (filters?.limit) params.set('limit', String(filters.limit));
      if (filters?.offset) params.set('offset', String(filters.offset));

      const response = await fetch(`${API_BASE}/api/curated-qa/pairs?${params}`, {
        headers: authHeaders
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Get a single curated Q&A pair by ID
   */
  async getCuratedQAPair(id: number): Promise<{
    success: boolean;
    pair?: any;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/curated-qa/pairs/${id}`, {
        headers: authHeaders
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Create a new curated Q&A pair
   */
  async createCuratedQAPair(qa: {
    question: string;
    answer: string;
    category: string;
    subcategory?: string;
    tags?: string[];
    keywords?: string[];
    priority?: number;
    quality_score?: number;
    language?: string;
    created_by?: string;
    tenant_id?: string;
  }): Promise<{
    success: boolean;
    id?: number;
    pair?: any;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/curated-qa/pairs`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(qa),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Update a curated Q&A pair
   */
  async updateCuratedQAPair(id: number, updates: any): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/curated-qa/pairs/${id}`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Delete a curated Q&A pair
   */
  async deleteCuratedQAPair(id: number): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/curated-qa/pairs/${id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Deactivate a curated Q&A pair
   */
  async deactivateCuratedQAPair(id: number): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/curated-qa/pairs/${id}/deactivate`, {
        method: 'POST',
        headers: authHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Search curated Q&A pairs
   */
  async searchCuratedQA(query: string, options?: {
    category?: string;
    tenant_id?: string;
    limit?: number;
    threshold?: number;
    use_semantic?: boolean;
    use_keyword?: boolean;
  }): Promise<{
    success: boolean;
    results?: any;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/curated-qa/search`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, ...options }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Get curated QA categories
   */
  async getCuratedQACategories(filters?: {
    is_active?: boolean;
    tenant_id?: string;
  }): Promise<{
    success: boolean;
    categories?: any[];
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const params = new URLSearchParams();
      if (filters?.is_active !== undefined) params.set('is_active', String(filters.is_active));
      if (filters?.tenant_id) params.set('tenant_id', filters.tenant_id);

      const response = await fetch(`${API_BASE}/api/curated-qa/categories?${params}`, {
        headers: authHeaders
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Get curated QA statistics
   */
  async getCuratedQAStats(filters?: {
    tenant_id?: string;
    category?: string;
  }): Promise<{
    success: boolean;
    stats?: any;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const params = new URLSearchParams();
      if (filters?.tenant_id) params.set('tenant_id', filters.tenant_id);
      if (filters?.category) params.set('category', filters.category);

      const response = await fetch(`${API_BASE}/api/curated-qa/stats?${params}`, {
        headers: authHeaders
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Get curated QA settings
   */
  async getCuratedQASettings(): Promise<{
    success: boolean;
    settings?: any;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/curated-qa/settings`, {
        headers: authHeaders
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Update curated QA settings
   */
  async updateCuratedQASettings(settings: Record<string, any>): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/curated-qa/settings`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Bulk import curated Q&A pairs
   */
  async bulkImportCuratedQA(qaPairs: Array<{
    question: string;
    answer: string;
    category: string;
    subcategory?: string;
    tags?: string[];
    keywords?: string[];
  }>, tenant_id?: string): Promise<{
    success: boolean;
    result?: any;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/curated-qa/bulk-import`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ qaPairs, tenant_id }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Submit feedback for a curated Q&A pair
   */
  async submitCuratedQAFeedback(feedback: {
    qa_pair_id: number;
    is_helpful?: boolean;
    rating?: number;
    feedback_text?: string;
    chat_id?: string;
    message_id?: string;
    tenant_id?: string;
  }): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/curated-qa/feedback`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(feedback),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  // ═══════════════════════════════════════════════════════════════════════
  // TENANT GUARDRAILS API
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Get tenant guardrails configuration
   */
  async getTenantGuardrails(tenantId: string): Promise<{
    ok: boolean;
    guardrails?: any;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/tenants/${tenantId}/guardrails`, {
        headers: authHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Update tenant guardrails configuration
   */
  async updateTenantGuardrails(tenantId: string, data: {
    entity_gate_enabled?: boolean;
    entity_gate_terms?: string[];
    entity_gate_custom_terms?: string[];
    mode_switching_enabled?: boolean;
    default_mode?: string;
    no_assumption_phrases?: string[];
    verifier_pass_enabled?: boolean;
  }): Promise<{
    ok: boolean;
    guardrails?: any;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/tenants/${tenantId}/guardrails`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Get all flow configurations for a tenant
   */
  async getTenantFlows(tenantId: string): Promise<{
    ok: boolean;
    flows?: any[];
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/tenants/${tenantId}/flows`, {
        headers: authHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Create a new flow configuration
   */
  async createTenantFlow(tenantId: string, data: {
    flow_name: string;
    intent_patterns: string[];
    mode: string;
    required_fields?: Record<string, boolean>;
    first_question_template?: string;
    question_order?: string[];
    rag_doc_type?: string;
    allow_answering?: boolean;
    priority?: number;
    enabled?: boolean;
  }): Promise<{
    ok: boolean;
    flow?: any;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/tenants/${tenantId}/flows`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Update a flow configuration
   */
  async updateTenantFlow(tenantId: string, flowId: number, data: {
    flow_name?: string;
    intent_patterns?: string[];
    mode?: string;
    required_fields?: Record<string, boolean>;
    first_question_template?: string;
    question_order?: string[];
    rag_doc_type?: string;
    allow_answering?: boolean;
    priority?: number;
    enabled?: boolean;
  }): Promise<{
    ok: boolean;
    flow?: any;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/tenants/${tenantId}/flows/${flowId}`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Delete a flow configuration
   */
  async deleteTenantFlow(tenantId: string, flowId: number): Promise<{
    ok: boolean;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/tenants/${tenantId}/flows/${flowId}`, {
        method: 'DELETE',
        headers: authHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Get conversation facts for a chat
   */
  async getConversationFacts(chatId: string, status?: string): Promise<{
    ok: boolean;
    facts?: any[];
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const url = status
        ? `${API_BASE}/api/admin/chats/${chatId}/facts?status=${status}`
        : `${API_BASE}/api/admin/chats/${chatId}/facts`;

      const response = await fetch(url, {
        headers: authHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Get all service configurations for current tenant
   */
  async getServiceConfigs(): Promise<{
    ok: boolean;
    configs?: Array<{
      id: number;
      tenant_id: string;
      service_name: string;
      service_code: string;
      keywords: string[];
      description: string;
      enabled: boolean;
      required_keys?: string[];
    }>;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/service-config`, {
        headers: authHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Update service configuration (including required_keys)
   */
  async updateServiceConfig(
    serviceCode: string,
    updates: {
      service_name?: string;
      keywords?: string[];
      description?: string;
      enabled?: boolean;
      required_keys?: string[];
    }
  ): Promise<{
    ok: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/service-config/${serviceCode}`, {
        method: "PUT",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Create new service configuration
   */
  async createServiceConfig(data: {
    service_name: string;
    service_code: string;
    keywords: string[];
    description?: string;
    enabled?: boolean;
  }): Promise<{
    ok: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/service-config`, {
        method: "POST",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Delete service configuration
   */
  async deleteServiceConfig(serviceCode: string): Promise<{
    ok: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/service-config/${serviceCode}`, {
        method: "DELETE",
        headers: authHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Get OpenRouter models list
   */
  async getOpenRouterModels(filters?: {
    search?: string;
    minContextLength?: number;
    maxPricePerMillion?: number;
  }): Promise<{
    ok: boolean;
    models?: Array<{
      id: string;
      name: string;
      description?: string;
      context_length?: number;
      pricing?: any;
    }>;
    count?: number;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.minContextLength) params.append('minContextLength', String(filters.minContextLength));
      if (filters?.maxPricePerMillion) params.append('maxPricePerMillion', String(filters.maxPricePerMillion));

      const queryString = params.toString();
      const url = `${API_BASE}/api/admin/openrouter/models${queryString ? '?' + queryString : ''}`;

      const response = await fetch(url, { headers: authHeaders });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  // ============================================================================
  // Qualification Flow Endpoints
  // ============================================================================

  /**
   * Get all qualification flows for tenant
   */
  async getQualificationFlows(): Promise<{
    ok: boolean;
    data?: any[];
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/qualification-flows`, {
        headers: authHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Get specific qualification flow with steps
   */
  async getQualificationFlow(domainKey: string): Promise<{
    ok: boolean;
    data?: {
      flow: any;
      steps: any[];
    };
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/qualification-flows/${domainKey}`, {
        headers: authHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Create or update qualification flow
   */
  async saveQualificationFlow(flowData: {
    domain_key: string;
    flow_name?: string;
    flow_type?: string;
    min_facts_required?: number;
    max_facts_required?: number;
    completion_action?: string;
    active?: boolean;
  }): Promise<{
    ok: boolean;
    message?: string;
    data?: any;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/qualification-flows`, {
        method: "POST",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(flowData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Delete qualification flow
   */
  async deleteQualificationFlow(domainKey: string): Promise<{
    ok: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/qualification-flows/${domainKey}`, {
        method: "DELETE",
        headers: authHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Add or update flow step
   */
  async saveFlowStep(domainKey: string, stepData: {
    step_order: number;
    field_name: string;
    field_label?: string;
    question_template: string;
    depends_on_field?: string;
    depends_on_value?: string;
    is_required?: boolean;
  }): Promise<{
    ok: boolean;
    message?: string;
    data?: any;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/qualification-flows/${domainKey}/steps`, {
        method: "POST",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(stepData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Delete flow step
   */
  async deleteFlowStep(domainKey: string, stepId: string): Promise<{
    ok: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/qualification-flows/${domainKey}/steps/${stepId}`, {
        method: "DELETE",
        headers: authHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Delete a WhatsApp message
   * Supports both "delete for everyone" and "delete for me"
   */
  async deleteWhatsAppMessage(tenantId: string, entityId: string, messageId: string, forEveryone: boolean = true, accessToken?: string): Promise<{
    success: boolean;
    message?: string;
    deleted?: boolean;
    for_everyone?: boolean;
    error?: string;
  }> {
    try {
      const authHeaders = accessToken
        ? { "Content-Type": "application/json", "Authorization": `Bearer ${accessToken}` }
        : await this.getAuthHeaders();

      if (!("Authorization" in authHeaders)) {
        return {
          success: false,
          error: "Authentication token is missing. Please refresh the page or sign in again.",
        };
      }

      const response = await fetch(
        `${API_BASE}/api/whatsapp/${encodeURIComponent(tenantId)}/${encodeURIComponent(entityId)}/messages/${encodeURIComponent(messageId)}?forEveryone=${forEveryone}`,
        {
          method: 'DELETE',
          headers: authHeaders,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.message || errorData.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Summarize conversations for a given date/time range
   * Generates LLM-based summaries with action items extraction
   */
  async summarizeChats(
    entityId: string,
    startTime: string,
    endTime: string,
    forceRefresh: boolean = false
  ): Promise<{
    summaries: Array<{
      chatId: string;
      chatName?: string;
      phoneNumber: string;
      messageCount: number;
      summary: string;
      actionItems: {
        unansweredQuestions: string[];
        followUps: string[];
        bookedMeetings: string[];
      };
      lastMessageAt: string;
      cached?: boolean;
    }>;
    totalChats: number;
    dateRange: {
      start: string;
      end: string;
    };
    cached: boolean;
    generatedAt: string;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/chats/summarize`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          entityId,
          startTime,
          endTime,
          forceRefresh,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.details || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to summarize chats:', error);
      return {
        summaries: [],
        totalChats: 0,
        dateRange: { start: startTime, end: endTime },
        cached: false,
        generatedAt: new Date().toISOString(),
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  },

  /**
   * Push saved chat summaries to Zoho CRM "WhatsApp Conversation" field
   * Searches leads by phone number and updates the field for each matched lead
   */
  async pushSummariesToZoho(
    entityId: string,
    startTime: string,
    endTime: string
  ): Promise<{
    ok: boolean;
    results: Array<{
      chatId: string;
      chatName: string;
      phoneNumber: string;
      status: 'success' | 'failed' | 'no_lead';
      zohoLeadId?: string;
      error?: string;
    }>;
    successCount: number;
    failedCount: number;
    noLeadCount: number;
    error?: string;
  }> {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/chats/summaries/push-to-zoho`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ entityId, startTime, endTime }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.details || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to push summaries to Zoho:', error);
      return {
        ok: false,
        results: [],
        successCount: 0,
        failedCount: 0,
        noLeadCount: 0,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  },

  // Get auth headers for authenticated requests.
  // Tries a cache-bypassing force-refresh if the first attempt yields no token,
  // so a stale cached token doesn't silently block every API call.
  async getAuthHeaders(): Promise<HeadersInit> {
    const headers = await getAuthHeaders();
    if (!(headers as Record<string, string>)['Authorization'] && rawGetAccessTokenFn) {
      try {
        const freshToken = await rawGetAccessTokenFn({ cacheMode: 'off' });
        if (freshToken) {
          return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${freshToken}` };
        }
      } catch {
        // Refresh token also expired — caller will receive the 401
      }
    }
    return headers;
  },

  async getZohoProducts(): Promise<string[]> {
    const response = await authFetch(`${API_BASE}/api/admin/zoho/products`);
    if (!response.ok) return [];
    const data = await response.json();
    return data.products || [];
  },

  async triggerZohoSync(): Promise<{ ok: boolean; results?: any[]; error?: string }> {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/api/admin/zoho/sync-products`, {
      method: 'POST',
      headers: authHeaders,
    });
    if (!response.ok) return { ok: false, error: `HTTP ${response.status}` };
    return { ok: true, ...(await response.json()) };
  },
};
